import { TextField, Tabs, Tab } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiUpdateProjectCatalog } from '~/providers/api';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IClientProjectCatalog, IClientProjectCatalogPayload } from "~/providers/client-interfaces";
import { IUrlParams } from "~/interfaces";
import WhatAbout from "~/components/WhatAbout";
import MyAlert from "~/mui/MyAlert";
import TextFieldLen from "~/mui/TextFieldLen";

import "./style.less";

const TABS = {
	CZECH: 0,
	ENGLISH: 1,
};
const RANGES = {
	/* eslint-disable-next-line */
	title: [10, 50],
	/* eslint-disable-next-line */
	desc: [50, 500],
};

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	changedPayloadCs: boolean;
	changedPayloadEn: boolean;
	wasChanged: boolean;
	internalName: string;
	setInternalName: boolean;
	tabInd: number;
	url: string;
	langsInfo: boolean;
}

interface IStatePayload extends Omit<IClientProjectCatalogPayload, "language"> {}
interface IFormError {
	titleCs: string;
	titleEn: string;
	subtitleCs: string;
	subtitleEn: string;
	descriptionCs: string;
	descriptionEn: string;
	apiDescriptionCs: string;
	apiDescriptionEn: string;
	url: string;
}

interface IEditProjectCatalogModal {
	params: IUrlParams;
	isInternal: boolean;
	catalogData: IClientProjectCatalog;
	onClose?: () => void;
	onSave?: (catalog: IClientProjectCatalog) => void;
	onChange?: (isChanged: boolean) => void;
}

const EMPTY_ERROR_STATE: IFormError = {
	titleCs: "",
	titleEn: "",
	subtitleCs: "",
	subtitleEn: "",
	descriptionCs: "",
	descriptionEn: "",
	apiDescriptionCs: "",
	apiDescriptionEn: "",
	url: "",
};

function getPayloadData(catalog: IClientProjectCatalog, isEnglish?: boolean): IStatePayload {
	if (catalog) {
		const curLang = isEnglish
			? "en"
			: "cs";
		const filtered = catalog.payload.filter(item => item.language === curLang);

		if (filtered.length === 1) {
			return filtered[0];
		}
	}

	return {
		title: "",
		subtitle: "",
		description: "",
		apiDescription: "",
	};
}

export default function EditProjectCatalogModal({
	params,
	isInternal = false,
	catalogData = null,
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IEditProjectCatalogModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const defCatalogUrl = catalogData?.referenceUrl || "";
	const defPayloadDataCs = getPayloadData(catalogData);
	const defPayloadDataEn = getPayloadData(catalogData, true);
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		changedPayloadCs: false,
		changedPayloadEn: false,
		wasChanged: false,
		internalName: "",
		setInternalName: false,
		tabInd: TABS.CZECH,
		url: defCatalogUrl,
		langsInfo: false,
	});
	const { state: statePayloadCs, updateState: updateStatePayloadCs } = myUseState<IStatePayload>(defPayloadDataCs);
	const { state: statePayloadEn, updateState: updateStatePayloadEn } = myUseState<IStatePayload>(defPayloadDataEn);
	const { state: stateFormError, setState: setStateFormError } = myUseState<IFormError>({
		...EMPTY_ERROR_STATE,
	});

	function isPayloadChanged(newData: IStatePayload, isEnglish?: boolean) {
		const source = isEnglish
			? defPayloadDataEn
			: defPayloadDataCs;

		return source.title !== newData.title
			|| source.subtitle !== newData.subtitle
			|| source.description !== newData.description
			|| source.apiDescription !== newData.apiDescription;
	}

	function updateDataWithChange(updatedData: Partial <IStatePayload>, isEnglish?: boolean) {
		let changedPayloadCs = state.changedPayloadCs;
		let changedPayloadEn = state.changedPayloadEn;

		if (isEnglish) {
			const newState = {
				...statePayloadEn,
				...updatedData,
			};

			changedPayloadEn = isPayloadChanged(newState, isEnglish);
			updateStatePayloadEn(newState);
		} else {
			const newState = {
				...statePayloadCs,
				...updatedData,
			};

			changedPayloadCs = isPayloadChanged(newState, isEnglish);
			updateStatePayloadCs(newState);
		}

		const wasChanged = changedPayloadCs || changedPayloadEn || state.url !== defCatalogUrl;

		updateState({
			changedPayloadCs,
			changedPayloadEn,
			wasChanged,
		});
		onChange(wasChanged);
	}

	function updateUrlWithChange(newUrl: string) {
		const wasChanged = state.changedPayloadCs || state.changedPayloadEn || newUrl !== defCatalogUrl;

		updateState({
			url: newUrl,
			wasChanged,
		});
		onChange(wasChanged);
	}

	function checkFormData() {
		const formData: IFormError = {
			titleCs: statePayloadCs.title,
			titleEn: statePayloadEn.title,
			subtitleCs: statePayloadCs.subtitle,
			subtitleEn: statePayloadEn.subtitle,
			descriptionCs: statePayloadCs.description,
			descriptionEn: statePayloadEn.description,
			apiDescriptionCs: statePayloadCs.apiDescription,
			apiDescriptionEn: statePayloadEn.apiDescription,
			url: state.url,
		};
		const errorData = checkForErrorsState<IFormError, IFormError>(formData, {
			...state.changedPayloadCs || (!state.changedPayloadCs && !state.changedPayloadEn)
				? {
					titleCs: { check: "string-10-50", msg: trans("editProjectCatalogModal.title.error") },
					subtitleCs: { check: "string-10-50", msg: trans("editProjectCatalogModal.title.error") },
					descriptionCs: { check: "string-50-500", msg: trans("editProjectCatalogModal.description.error") },
					apiDescriptionCs: { check: "string-50-500", msg: trans("editProjectCatalogModal.description.error") },
				}
				: {},
			...state.changedPayloadEn
				? {
					titleEn: { check: "string-10-50", msg: trans("editProjectCatalogModal.title.error") },
					subtitleEn: { check: "string-10-50", msg: trans("editProjectCatalogModal.title.error") },
					descriptionEn: { check: "string-50-500", msg: trans("editProjectCatalogModal.description.error") },
					apiDescriptionEn: { check: "string-50-500", msg: trans("editProjectCatalogModal.description.error") },
				}
				: {},
			url: { check: "url", msg: trans("editProjectCatalogModal.url.error") },
		});

		setStateFormError({
			...EMPTY_ERROR_STATE,
			...errorData.errorsState,
		});

		if (errorData.errorsCount > 0) {
			return false;
		}

		return true;
	}

	async function dialogOnSave() {
		if (!checkFormData()) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const outputData = await apiUpdateProjectCatalog(isInternal ? "admin" : "client", params.projectId, {
			referenceUrl: state.url,
			payload: [
				// tady staci test na 1 parametr
				...statePayloadCs.title
					? [{
						language: "cs",
						...statePayloadCs,
					}]
					: [],
				...statePayloadEn.title
					? [{
						language: "en",
						...statePayloadEn,
					}]
					: [],
			],
		}, lang);

		if (outputData.noErrors) {
			updateState({
				dialogLoading: false,
			});
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	function getCzechTabErrors() {
		return stateFormError.titleCs
			|| stateFormError.subtitleCs
			|| stateFormError.descriptionCs
			|| stateFormError.apiDescriptionCs;
	}

	function getEnglishTabErrors() {
		return stateFormError.titleEn
			|| stateFormError.subtitleEn
			|| stateFormError.descriptionEn
			|| stateFormError.apiDescriptionEn;
	}

	function showLangsInfo() {
		updateState({
			langsInfo: true,
		});
	}

	return <MyDialog title={catalogData ? trans("editProjectCatalogModal.mainTitle") : trans("projectCatalogPage.createCatalogTitle")} onClose={onClose} onSave={dialogOnSave} contentClassName="editProjectCatalogModal" dialogLoading={state.dialogLoading}
		errorMsg={state.errorMsg} saveCaption={trans("saveChanges")} cancelCaption={trans("close")} confirmClose={state.wasChanged}>
		<div>
			<TextField
				label={trans("editProjectCatalogModal.url")}
				value={state.url}
				style={{ width: "100%" }}
				onChange={event => updateUrlWithChange(event.target.value)}
				color="success"
				autoComplete="off"
				error={stateFormError.url !== ""}
				helperText={stateFormError.url || trans("editProjectCatalogModal.url.desc")}
				size="small"
			/>
		</div>
		<Tabs value={ state.tabInd } onChange={(___, tabInd) => updateState({ tabInd })}>
			<Tab label={trans("bulkEmailModal.tab2")} icon={getCzechTabErrors() && <ErrorIcon />} iconPosition="end" />
			<Tab label={trans("bulkEmailModal.tab3")} icon={getEnglishTabErrors() && <ErrorIcon />} iconPosition="end" />
		</Tabs>
		<div style={{ position: "relative" }}>
			<div className="editProjectCatalogModal__whatAboutLine">
				<WhatAbout title={trans("editProjectCatalogModal.whatAbout.langs")} onClick={showLangsInfo} />
			</div>
		</div>
		{ state.tabInd === TABS.CZECH && <div className="editProjectCatalogModal__tabContent">
			<TextFieldLen
				label={trans("editProjectCatalogModal.title")}
				lenRange={RANGES.title}
				value={statePayloadCs.title}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ title: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.titleCs !== ""}
				helperText={stateFormError.titleCs || trans("editProjectCatalogModal.title.desc")}
				size="small"
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.subtitle")}
				lenRange={RANGES.title}
				value={statePayloadCs.subtitle}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ subtitle: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.subtitleCs !== ""}
				helperText={stateFormError.subtitleCs || trans("editProjectCatalogModal.subtitle.desc")}
				size="small"
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.description")}
				lenRange={RANGES.desc}
				value={statePayloadCs.description}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ description: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.descriptionCs !== ""}
				helperText={stateFormError.descriptionCs || trans("editProjectCatalogModal.description.desc")}
				size="small"
				multiline={true}
				rows={5}
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.apiDescription")}
				lenRange={RANGES.desc}
				value={statePayloadCs.apiDescription}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ apiDescription: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.apiDescriptionCs !== ""}
				helperText={stateFormError.apiDescriptionCs || trans("editProjectCatalogModal.apiDescription.desc")}
				size="small"
				multiline={true}
				rows={5}
			/>
		</div>}
		{ state.tabInd === TABS.ENGLISH && <div className="editProjectCatalogModal__tabContent">
			<TextFieldLen
				label={trans("editProjectCatalogModal.title")}
				lenRange={RANGES.title}
				value={statePayloadEn.title}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ title: event.target.value }, true)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.titleEn !== ""}
				helperText={stateFormError.titleEn || trans("editProjectCatalogModal.title.desc")}
				size="small"
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.subtitle")}
				lenRange={RANGES.title}
				value={statePayloadEn.subtitle}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ subtitle: event.target.value }, true)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.subtitleEn !== ""}
				helperText={stateFormError.subtitleEn || trans("editProjectCatalogModal.subtitle.desc")}
				size="small"
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.description")}
				lenRange={RANGES.desc}
				value={statePayloadEn.description}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ description: event.target.value }, true)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.descriptionEn !== ""}
				helperText={stateFormError.descriptionEn || trans("editProjectCatalogModal.description.desc")}
				size="small"
				multiline={true}
				rows={5}
			/>
			<TextFieldLen
				label={trans("editProjectCatalogModal.apiDescription")}
				lenRange={RANGES.desc}
				value={statePayloadEn.apiDescription}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ apiDescription: event.target.value }, true)}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.apiDescriptionEn !== ""}
				helperText={stateFormError.apiDescriptionEn || trans("editProjectCatalogModal.apiDescription.desc")}
				size="small"
				multiline={true}
				rows={5}
			/>
		</div>}
		{ state.langsInfo && <MyAlert title={trans("catalog.langsInfo.title")} onClose={() => updateState({ langsInfo: false })} cancelCaption={trans("close")}>
			<p>{ trans("catalog.langsInfo.desc") }</p>
		</MyAlert> }
	</MyDialog>;
}
